import React from "react";
import { defaultPfpUrl } from "../utils";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { ScrollArea } from "../ui/scroll-area";
import { Skeleton } from "../ui/skeleton";
import { cn } from "../../lib/utils";
import { ChatItem } from "./ChatList";
import HighlightedText from "./HighlightedText";
import { getSharedMessagePreview } from "./chatListUtils";
import { previewMentionDisplayText } from "../../lib/mentions";
import { useNow } from "../../hooks/useNow";

/**
 * Loading placeholders for inbox search.
 * @returns {JSX.Element}
 */
function InboxSearchSkeleton() {
  return (
    <div className="space-y-3 p-4" aria-hidden="true">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="h-12 w-12 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3 max-w-[12rem]" />
            <Skeleton className="h-3 w-full max-w-[14rem]" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * A matching message in inbox search.
 * @param {{
 *   hit: object,
 *   query: string,
 *   onSelect: (hit: object) => void,
 * }} props
 * @returns {JSX.Element}
 */
function MessageSearchItem({ hit, query, onSelect }) {
  const preview = previewMentionDisplayText(
    getSharedMessagePreview(hit) || hit.content || "",
    hit.mentions
  );

  return (
    <button
      type="button"
      onClick={() => onSelect(hit)}
      className="flex w-full cursor-pointer items-start rounded-md p-3 text-left transition-colors hover:bg-chat-list-row-hover"
    >
      <Avatar className="mr-3 h-10 w-10 shrink-0">
        <AvatarImage
          src={hit.sender_pfp || defaultPfpUrl}
          alt={hit.sender_name || ""}
          onError={(e) => {
            e.target.src = defaultPfpUrl;
          }}
        />
        <AvatarFallback className="text-xs">
          {(hit.sender_name || "?").slice(0, 2).toUpperCase()}
        </AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-ui-foreground">
          <HighlightedText text={hit.sender_name || ""} query={query} />
        </div>
        {preview ? (
          <p className="mt-0.5 line-clamp-2 text-sm text-ui-muted-foreground">
            <HighlightedText text={preview} query={query} />
          </p>
        ) : null}
        <p className="mt-1 truncate text-xs text-text-muted">
          <HighlightedText text={hit.chat_name || "Chat"} query={query} />
        </p>
      </div>
    </button>
  );
}

/**
 * Chat + message search results shown in place of the open thread.
 * The Chats section is omitted when there are no chat hits.
 *
 * @param {{
 *   userId: number,
 *   query: string,
 *   chats: Array<object>,
 *   messages: Array<object>,
 *   loading: boolean,
 *   error?: string,
 *   onSelectChat: (chat: object) => void,
 *   onSelectMessage: (hit: object) => void,
 *   onChatRead?: (chatId: number) => void,
 *   onBack?: () => void,
 *   chatsLabel?: string,
 *   emptyLabel?: string,
 * }} props
 * @returns {JSX.Element}
 */
function InboxSearchResults({
  userId,
  query,
  chats,
  messages,
  loading,
  error = "",
  onSelectChat,
  onSelectMessage,
  onChatRead,
  onBack,
  chatsLabel = "Chats",
  emptyLabel = "No chats or messages match your search.",
}) {
  const now = useNow();
  const hasChats = chats.length > 0;
  const hasMessages = messages.length > 0;

  return (
    <div className="flex h-full min-h-0 flex-col bg-primary">
      {onBack ? (
        <div className="flex shrink-0 items-center gap-2 border-b border-ui-border px-3 py-2 md:hidden">
          <button
            type="button"
            onClick={onBack}
            className="rounded-md px-2 py-1 text-sm text-text-accent hover:text-text-accent-hover"
          >
            Back
          </button>
          <h2 className="text-sm font-semibold text-text-primary">Search results</h2>
        </div>
      ) : (
        <div className="hidden shrink-0 border-b border-ui-border px-4 py-3 md:block">
          <h2 className="text-sm font-semibold text-text-primary">Search results</h2>
        </div>
      )}

      {loading && !hasChats && !hasMessages ? (
        <InboxSearchSkeleton />
      ) : error ? (
        <p className="p-4 text-sm text-text-muted">{error}</p>
      ) : !hasChats && !hasMessages ? (
        <p className="p-4 text-sm text-text-muted">{emptyLabel}</p>
      ) : (
        <ScrollArea className="h-full max-h-full min-h-0 w-full">
          <div className="space-y-4 p-2 pb-6">
            {hasChats ? (
              <section>
                <h3 className="px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
                  {chatsLabel}
                </h3>
                <div className="space-y-0.5">
                  {chats.map((chat) => (
                    <ChatItem
                      key={chat.id}
                      userId={userId}
                      chat={chat}
                      isSelected={false}
                      onChatSelect={onSelectChat}
                      onChatRead={onChatRead}
                      highlightQuery={query}
                      now={now}
                    />
                  ))}
                </div>
              </section>
            ) : null}

            {hasMessages ? (
              <section>
                <h3
                  className={cn(
                    "px-3 pb-1 text-xs font-semibold uppercase tracking-wide text-text-muted",
                    hasChats && "pt-2"
                  )}
                >
                  Messages
                </h3>
                <div className="space-y-0.5">
                  {messages.map((hit) => (
                    <MessageSearchItem
                      key={hit.id}
                      hit={hit}
                      query={query}
                      onSelect={onSelectMessage}
                    />
                  ))}
                </div>
              </section>
            ) : null}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}

export default InboxSearchResults;
